/**
 * The quote engine.
 *
 * Pure functions. No database, no network, no AI, no clock — every input is an
 * argument and the same inputs always produce the same output. Everything else
 * in this project can be rebuilt from the schema; if this file is wrong, the
 * business quotes wrong, and nobody finds out until a couple has signed.
 *
 * ── Money ──────────────────────────────────────────────────────────────────
 *
 * All arithmetic happens in INTEGER CENTS. Rand amounts are converted on the
 * way in and back on the way out, and nothing in between is a fractional rand.
 * This is not fussiness: `0.1 + 0.2 !== 0.3` in binary floating point, and a
 * quote is a column of numbers that has to add up to its own total when a
 * client checks it with a calculator.
 *
 * Rounding happens at exactly one place — the moment a line item is emitted —
 * and every later percentage is taken on the sum of already-rounded lines.
 *
 * The brief (§6.4) says to round only at the very end and never intermediately.
 * That instruction, followed literally, produces quotes whose printed column
 * does not sum to the printed total: three lines of 47.335, 1250.005 and
 * 8999.991 print as 47.34, 1250.01 and 8999.99, which a client adds to
 * 10 297.34, while an unrounded total prints 10 297.33. The discrepancy is one
 * cent and it is the kind of thing that costs trust at exactly the wrong
 * moment. Rounding per line and summing the rounded values is standard
 * invoicing practice and what SARS expects on a tax invoice. Recorded in
 * docs/decisions.md §4.
 *
 * ── Order of operations ────────────────────────────────────────────────────
 *
 * Fixed by the brief and not negotiable, because changing it changes prices:
 *
 *   1. Package base price
 *   2. Add-ons, at quantity × unit price
 *   3. Travel, beyond the free radius, doubled for the return leg
 *   4. Accommodation, a flat fee once past the threshold
 *   5. Peak season surcharge, as a percentage
 *   6. Weekday discount, as a percentage
 *   7. Manual adjustments
 *   8. VAT, if the business is registered
 *   9. Minimum booking value — FLAGGED, never silently applied
 *
 * Every step emits a labelled line with its working shown. There are no hidden
 * calculations: if a number appears on a quote, this file can say where it came
 * from, in a sentence, in the `detail` field.
 */

import { isWeekdayBooking, monthOf, type IsoDate } from './dates'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LineSource =
  | 'package'
  | 'addon'
  | 'travel'
  | 'accommodation'
  | 'surcharge'
  | 'discount'
  | 'manual'
  | 'vat'

export interface QuoteLine {
  source: LineSource
  description: string
  /**
   * The package or add-on this line came from, where there is one. Carried so a
   * saved draft can be reopened with the right boxes ticked; the PRICE in this
   * line is frozen and is never read back from that record.
   */
  sourceId?: string | undefined
  /** 1 for anything that is not genuinely a multiple. */
  quantity: number
  /** Rand. Negative for a discount. */
  unitPrice: number
  /** Rand, rounded to cents. quantity × unitPrice, except where a percentage. */
  total: number
  /**
   * The working, in one sentence, for a human reading the quote builder:
   * '412 km, less the first 50 km, doubled for the return trip, at R6.50/km'.
   * Never printed on the client's PDF unless she chooses to show it.
   */
  detail?: string
}

export type FlagCode =
  | 'accommodation_needed'
  | 'below_minimum'
  | 'missing_distance'
  | 'no_package'
  | 'unpriced_travel'
  | 'zero_total'

export interface QuoteFlag {
  code: FlagCode
  message: string
  /** `blocking` means do not send this quote; `warning` means look at it first. */
  severity: 'warning' | 'blocking'
}

export interface PricingRules {
  /** One-way kilometres travelled before travel is charged at all. */
  freeTravelRadiusKm: number
  travelRatePerKm: number
  /** When true the chargeable distance is doubled, because she drives home again. */
  travelChargesReturnTrip: boolean
  /** One-way kilometres beyond which an overnight stay is needed. */
  accommodationThresholdKm: number
  accommodationFlatFee: number
  /** 1-based months, e.g. [11, 12, 1, 2, 3] for the Cape summer. */
  peakSeasonMonths: number[]
  /** Fraction, not percent. 0.1 is 10%. */
  peakSeasonSurchargePct: number
  /** Fraction. Applies Monday to Thursday. */
  weekdayDiscountPct: number
  minimumBookingValue: number
}

export interface QuotePackage {
  id: string
  name: string
  base_price: number
  /**
   * One-way kilometres included for this collection only, overriding the
   * rule: a wedding includes 100 km, a couple shoot 30. Null or absent uses
   * `rules.freeTravelRadiusKm`.
   */
  free_travel_radius_km?: number | null
}

export interface QuoteAddon {
  id: string
  name: string
  unit_price: number
  unit_type: 'each' | 'hour' | 'day' | 'person'
}

export interface ManualAdjustment {
  description: string
  /** Rand. Negative for a discount. */
  amount: number
}

export interface QuoteInput {
  package: QuotePackage | null
  addons: Array<{ addon: QuoteAddon; quantity: number }>
  /** One-way kilometres. Null means nobody has measured the venue. */
  distanceKm: number | null
  eventDate: IsoDate
  rules: PricingRules
  manualAdjustments: ManualAdjustment[]
  vat: { registered: boolean; rate: number }
}

export interface QuoteBreakdown {
  lines: QuoteLine[]
  /** Rand, everything before VAT. */
  subtotal: number
  vat: number
  total: number
  flags: QuoteFlag[]
}

// ---------------------------------------------------------------------------
// Cents
// ---------------------------------------------------------------------------

/**
 * Rand to integer cents. Rounds half away from zero, which is what an accountant
 * means by rounding and what SARS expects, unlike Math.round which rounds -0.5
 * up to -0.
 */
export function toCents(rand: number): number {
  const scaled = rand * 100
  return scaled < 0 ? -Math.round(-scaled) : Math.round(scaled)
}

export function toRand(cents: number): number {
  return cents / 100
}

/** Half away from zero, for values already in cents. */
function roundCents(cents: number): number {
  return cents < 0 ? -Math.round(-cents) : Math.round(cents)
}

// ---------------------------------------------------------------------------
// The engine
// ---------------------------------------------------------------------------

export function calculateQuote(input: QuoteInput): QuoteBreakdown {
  const { package: pkg, addons, distanceKm, eventDate, rules, manualAdjustments, vat } = input

  const lines: QuoteLine[] = []
  const flags: QuoteFlag[] = []

  /**
   * Emits a line, rounding once, and returns its value in cents.
   *
   * `detail` is spelled `string | undefined` rather than just optional because
   * exactOptionalPropertyTypes distinguishes "absent" from "present and
   * undefined", and several callers pass a conditional that evaluates to
   * undefined. The spread below then drops the key entirely, so a line without
   * working shown has no `detail` property at all rather than an undefined one.
   */
  function emit(
    line: Omit<QuoteLine, 'total' | 'detail' | 'sourceId'> & {
      totalCents: number
      detail?: string | undefined
      sourceId?: string | undefined
    },
  ): number {
    const cents = roundCents(line.totalCents)
    lines.push({
      source: line.source,
      description: line.description,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      total: toRand(cents),
      ...(line.detail === undefined ? {} : { detail: line.detail }),
      ...(line.sourceId === undefined ? {} : { sourceId: line.sourceId }),
    })
    return cents
  }

  let runningCents = 0

  // ── 1. Package ───────────────────────────────────────────────────────────

  if (pkg) {
    runningCents += emit({
      source: 'package',
      description: pkg.name,
      quantity: 1,
      unitPrice: pkg.base_price,
      totalCents: toCents(pkg.base_price),
      sourceId: pkg.id,
    })
  } else {
    flags.push({
      code: 'no_package',
      message: 'No collection chosen. Pick one before sending this quote.',
      severity: 'blocking',
    })
  }

  // ── 2. Add-ons ───────────────────────────────────────────────────────────

  for (const { addon, quantity } of addons) {
    if (quantity <= 0) continue

    runningCents += emit({
      source: 'addon',
      description: quantity === 1 ? addon.name : `${addon.name} × ${formatQuantity(quantity)}`,
      quantity,
      unitPrice: addon.unit_price,
      totalCents: toCents(addon.unit_price) * quantity,
      sourceId: addon.id,
      detail:
        quantity === 1
          ? undefined
          : `${formatQuantity(quantity)} ${unitLabel(addon.unit_type, quantity)} at ${money(addon.unit_price)} each`,
    })
  }

  // ── 3. Travel ────────────────────────────────────────────────────────────
  //
  // Charged on the distance beyond the free radius, doubled for the return leg
  // unless a rule says otherwise. A null distance is not zero travel — it is an
  // unknown, and quoting an unknown as free is exactly the mistake this flag
  // exists to prevent. The free radius is the collection's own where it has
  // one (a couple shoot includes less than a wedding).

  const freeRadiusKm = pkg?.free_travel_radius_km ?? rules.freeTravelRadiusKm

  if (distanceKm === null) {
    flags.push({
      code: 'missing_distance',
      message:
        'No distance for this venue, so no travel has been added. Measure the venue, or enter the distance on the wedding, before sending.',
      severity: 'warning',
    })
  } else {
    const chargeableOneWay = Math.max(0, distanceKm - freeRadiusKm)
    const multiplier = rules.travelChargesReturnTrip ? 2 : 1
    const chargeableKm = chargeableOneWay * multiplier

    if (chargeableKm > 0) {
      if (rules.travelRatePerKm <= 0) {
        flags.push({
          code: 'unpriced_travel',
          message: `This venue is ${round1(chargeableOneWay)} km beyond the free radius, but the travel rate is zero, so nothing has been charged.`,
          severity: 'warning',
        })
      } else {
        runningCents += emit({
          source: 'travel',
          description: 'Travel',
          quantity: round1(chargeableKm),
          unitPrice: rules.travelRatePerKm,
          totalCents: toCents(rules.travelRatePerKm) * chargeableKm,
          detail:
            `${round1(distanceKm)} km each way, less the first ${round1(freeRadiusKm)} km` +
            (rules.travelChargesReturnTrip ? ', doubled for the return trip' : '') +
            `, at ${money(rules.travelRatePerKm)}/km`,
        })
      }
    }

    // ── 4. Accommodation ───────────────────────────────────────────────────
    //
    // With a flat fee, a line. Without one (the 2027 guide: accommodation for
    // the team is excluded and arranged separately), a warning, so an overnight
    // stay is never forgotten just because it is not on the price.

    if (distanceKm > rules.accommodationThresholdKm) {
      if (rules.accommodationFlatFee > 0) {
        runningCents += emit({
          source: 'accommodation',
          description: 'Accommodation',
          quantity: 1,
          unitPrice: rules.accommodationFlatFee,
          totalCents: toCents(rules.accommodationFlatFee),
          detail: `${round1(distanceKm)} km is beyond the ${round1(rules.accommodationThresholdKm)} km mark, so an overnight stay is needed`,
        })
      } else {
        flags.push({
          code: 'accommodation_needed',
          message: `${round1(distanceKm)} km is beyond the ${round1(rules.accommodationThresholdKm)} km mark, so the team needs accommodation. It is not on this quote: arrange it with the couple.`,
          severity: 'warning',
        })
      }
    }
  }

  // ── 5. Peak season surcharge ─────────────────────────────────────────────
  //
  // A percentage of everything above, which is why order matters: a surcharge
  // taken before add-ons would be a different number.

  const month = monthOf(eventDate)

  if (rules.peakSeasonMonths.includes(month) && rules.peakSeasonSurchargePct > 0) {
    const surcharge = runningCents * rules.peakSeasonSurchargePct
    runningCents += emit({
      source: 'surcharge',
      description: 'Peak season',
      quantity: 1,
      unitPrice: toRand(roundCents(surcharge)),
      totalCents: surcharge,
      detail: `${percent(rules.peakSeasonSurchargePct)} of ${money(toRand(runningCents))}, for a ${monthName(month)} wedding`,
    })
  }

  // ── 6. Weekday discount ──────────────────────────────────────────────────

  if (isWeekdayBooking(eventDate) && rules.weekdayDiscountPct > 0) {
    const discount = -(runningCents * rules.weekdayDiscountPct)
    runningCents += emit({
      source: 'discount',
      description: 'Weekday discount',
      quantity: 1,
      unitPrice: toRand(roundCents(discount)),
      totalCents: discount,
      detail: `${percent(rules.weekdayDiscountPct)} off, for a ${dayName(eventDate)} wedding`,
    })
  }

  // ── 7. Manual adjustments ────────────────────────────────────────────────

  for (const adjustment of manualAdjustments) {
    if (adjustment.amount === 0) continue

    runningCents += emit({
      source: 'manual',
      description: adjustment.description,
      quantity: 1,
      unitPrice: adjustment.amount,
      totalCents: toCents(adjustment.amount),
    })
  }

  const subtotalCents = runningCents

  // ── 8. VAT ───────────────────────────────────────────────────────────────

  let vatCents = 0
  if (vat.registered && vat.rate > 0) {
    vatCents = roundCents(subtotalCents * vat.rate)
    lines.push({
      source: 'vat',
      description: `VAT at ${percent(vat.rate)}`,
      quantity: 1,
      unitPrice: toRand(vatCents),
      total: toRand(vatCents),
      detail: `${percent(vat.rate)} of ${money(toRand(subtotalCents))}`,
    })
  }

  const totalCents = subtotalCents + vatCents

  // ── 9. Minimum booking value ─────────────────────────────────────────────
  //
  // Flagged, never applied. Silently raising a quote to a floor is how a client
  // ends up paying a number nobody can explain to them.

  const minimumCents = toCents(rules.minimumBookingValue)
  if (minimumCents > 0 && totalCents > 0 && totalCents < minimumCents) {
    flags.push({
      code: 'below_minimum',
      message: `This comes to ${money(toRand(totalCents))}, below the ${money(rules.minimumBookingValue)} minimum. Adjust it deliberately or send it as it is — nothing has been changed automatically.`,
      severity: 'warning',
    })
  }

  if (totalCents === 0 && lines.length === 0) {
    flags.push({
      code: 'zero_total',
      message: 'This quote is empty.',
      severity: 'blocking',
    })
  }

  return {
    lines,
    subtotal: toRand(subtotalCents),
    vat: toRand(vatCents),
    total: toRand(totalCents),
    flags,
  }
}

// ---------------------------------------------------------------------------
// Resolving the rule rows into the object the engine takes
// ---------------------------------------------------------------------------

export interface PricingRuleRow {
  type: string
  numeric_value: number | string | null
  json_value: unknown
  effective_from: string
}

/** Sensible zeroes, so a missing rule charges nothing rather than throwing. */
export const NO_RULES: PricingRules = {
  freeTravelRadiusKm: 0,
  travelRatePerKm: 0,
  travelChargesReturnTrip: true,
  accommodationThresholdKm: Number.POSITIVE_INFINITY,
  accommodationFlatFee: 0,
  peakSeasonMonths: [],
  peakSeasonSurchargePct: 0,
  weekdayDiscountPct: 0,
  minimumBookingValue: 0,
}

/**
 * Picks the rule rows in force on a given date and shapes them into
 * `PricingRules`.
 *
 * Pure: takes the rows, returns the object. Rules are versioned by
 * `effective_from`, so the newest row on or before the quote date wins and
 * raising a travel rate next season leaves this season's quotes alone.
 */
export function resolveRules(rows: PricingRuleRow[], onDate: IsoDate): PricingRules {
  const latest = new Map<string, PricingRuleRow>()

  for (const row of rows) {
    if (row.effective_from > onDate) continue

    const current = latest.get(row.type)
    if (!current || row.effective_from > current.effective_from) {
      latest.set(row.type, row)
    }
  }

  const num = (type: string, fallback: number): number => {
    const row = latest.get(type)
    if (!row || row.numeric_value === null) return fallback
    const value = Number(row.numeric_value)
    return Number.isFinite(value) ? value : fallback
  }

  const months = (() => {
    const row = latest.get('peak_season_months')
    if (!row || !Array.isArray(row.json_value)) return []
    return row.json_value
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value >= 1 && value <= 12)
  })()

  return {
    freeTravelRadiusKm: num('free_travel_radius_km', NO_RULES.freeTravelRadiusKm),
    travelRatePerKm: num('travel_rate_per_km', NO_RULES.travelRatePerKm),
    travelChargesReturnTrip: num('travel_charges_return_trip', 1) !== 0,
    accommodationThresholdKm: num('accommodation_threshold_km', NO_RULES.accommodationThresholdKm),
    accommodationFlatFee: num('accommodation_flat_fee', NO_RULES.accommodationFlatFee),
    peakSeasonMonths: months,
    peakSeasonSurchargePct: num('peak_season_surcharge_pct', NO_RULES.peakSeasonSurchargePct),
    weekdayDiscountPct: num('weekday_discount_pct', NO_RULES.weekdayDiscountPct),
    minimumBookingValue: num('minimum_booking_value', NO_RULES.minimumBookingValue),
  }
}

// ---------------------------------------------------------------------------
// Small helpers, local so this file stays free of imports it does not need
// ---------------------------------------------------------------------------

function round1(value: number): number {
  return Math.round(value * 10) / 10
}

function money(rand: number): string {
  return `R${Math.abs(rand)
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    .replace(/ (?=\d{2}$)/, '')}`
}

function percent(fraction: number): string {
  return `${(fraction * 100).toFixed(2).replace(/\.?0+$/, '')}%`
}

function formatQuantity(quantity: number): string {
  return Number.isInteger(quantity) ? String(quantity) : quantity.toFixed(1)
}

function unitLabel(unit: QuoteAddon['unit_type'], quantity: number): string {
  const plural = quantity !== 1
  switch (unit) {
    case 'hour':
      return plural ? 'hours' : 'hour'
    case 'day':
      return plural ? 'days' : 'day'
    case 'person':
      return plural ? 'people' : 'person'
    case 'each':
    default:
      return plural ? 'items' : 'item'
  }
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

function monthName(month: number): string {
  return MONTHS[month - 1] ?? 'peak season'
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const

function dayName(date: IsoDate): string {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number)
  return DAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()] ?? 'weekday'
}
